import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router';

import { SP_COOKIE_BANNER_EVENTS } from 'Utilities/analytics/analyticsCookieBanner';
import { EVENT } from 'Utilities/analytics/optimizely/constants';
import { useOptimizelyTrack } from 'Utilities/analytics/optimizely/useOptimizelyTrack';
import { useIsEmbeddedView } from 'Utilities/hooks/useIsEmbeddedView';

import { ConsentMethod, postConsentDecision } from './consentService';
import { CookieBanner } from './CookieBanner';
import { ACCEPT_ALL_CONSENT, COOKIE_CONSENT_INFERRED_EVENT, COOKIE_POLICY_PATH, ESSENTIALS_ONLY_CONSENT } from './cookieConsent.constants';
import { applyConsent, hasStoredConsent } from './cookieConsent.helpers';

import styles from './CookieConsent.module.scss';

const OPTIONAL = 'optional';
const INFERRED = 'inferred';

export const CookieConsent = () => {
  const isEmbeddedView = useIsEmbeddedView();
  const { OPTIMIZELY_TRACK } = useOptimizelyTrack();
  const { pathname } = useLocation();
  const [visible, setVisible] = useState(false);
  const canShowBanner = visible && !isEmbeddedView;

  useEffect(() => {
    setVisible(!hasStoredConsent());
  }, []);

  useEffect(() => {
    if (canShowBanner) {
      SP_COOKIE_BANNER_EVENTS.LOADED(OPTIONAL);
    }
  }, [pathname, canShowBanner]);

  const handleAcceptAll = useCallback((label: string, method: ConsentMethod) => {
    SP_COOKIE_BANNER_EVENTS.ACCEPT_ALL_CLICKED(label);
    OPTIMIZELY_TRACK(EVENT.COOKIE_BANNER_ACCEPT_ALL_CLICKED);
    applyConsent(ACCEPT_ALL_CONSENT);
    postConsentDecision(ACCEPT_ALL_CONSENT, method);
    setVisible(false);
  }, [OPTIMIZELY_TRACK]);

  useEffect(() => {
    const handleInferredClick = () => {
      if (canShowBanner) {
        handleAcceptAll(INFERRED, 'inferred_accept_all');
      }
    };
    window.addEventListener(COOKIE_CONSENT_INFERRED_EVENT, handleInferredClick);
    return () => window.removeEventListener(COOKIE_CONSENT_INFERRED_EVENT, handleInferredClick);
  }, [handleAcceptAll, canShowBanner]);

  const handleAcceptClick = () => {
    handleAcceptAll(OPTIONAL, 'banner_accept_all');
  };

  const handleEssentialOnly = () => {
    SP_COOKIE_BANNER_EVENTS.ESSENTIAL_ONLY_CLICKED(OPTIONAL);
    OPTIMIZELY_TRACK(EVENT.COOKIE_BANNER_ESSENTIAL_ONLY_CLICKED);
    applyConsent(ESSENTIALS_ONLY_CONSENT);
    postConsentDecision(ESSENTIALS_ONLY_CONSENT, 'banner_essential_only');
    setVisible(false);
  };

  const handleLearnMore = () => {
    SP_COOKIE_BANNER_EVENTS.LEARN_MORE_CLICKED(COOKIE_POLICY_PATH, OPTIONAL);
    OPTIMIZELY_TRACK(EVENT.COOKIE_BANNER_LEARN_MORE_CLICKED);
  };

  if (!canShowBanner) {
    return null;
  }

  return (
    <div className={styles.overlay}>
      <CookieBanner
        onAcceptAll={handleAcceptClick}
        onEssentialOnly={handleEssentialOnly}
        onLearnMore={handleLearnMore}
      />
    </div>
  );
};
